import { Table, Space, Button, Tooltip } from "antd";
import { EyeOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import "./DataTable.css";

/**
 * DataTable — Ant Design Table wrapper with built-in patterns.
 *
 * Features:
 *   - Pagination (default 25 per page)
 *   - Sorting on all columns
 *   - Row selection with checkboxes (optional)
 *   - Action column with View/Edit/Delete icons (permission-controlled)
 *   - Loading state
 *
 * Usage:
 *   <DataTable
 *     columns={columns}
 *     dataSource={riders}
 *     loading={loading}
 *     pagination={{ current: page, pageSize: 25, total: totalCount }}
 *     onPageChange={(page, pageSize) => fetchData(page, pageSize)}
 *     rowSelection={{ selectedRowKeys, onChange: setSelectedRowKeys }}
 *     actions={{
 *       onView: (record) => ...,
 *       onEdit: (record) => ...,
 *       onDelete: (record) => ...,
 *       canEdit: true,
 *       canDelete: true,
 *     }}
 *   />
 */
export default function DataTable({
  columns = [],
  dataSource = [],
  loading = false,
  pagination = {},
  onPageChange,
  rowSelection,
  actions,
  rowKey = "_id",
  scroll,
  size = "middle",
  ...restProps
}) {
  // Build action column if actions are provided
  const actionColumn = actions
    ? {
        title: "Actions",
        key: "actions",
        width: 120,
        fixed: "right",
        render: (_, record) => {
          const editDisabled = typeof actions.isEditDisabled === "function"
            ? actions.isEditDisabled(record)
            : false;
          const deleteDisabled = typeof actions.isDeleteDisabled === "function"
            ? actions.isDeleteDisabled(record)
            : false;
          const deleteReason = typeof actions.getDeleteDisabledReason === "function"
            ? actions.getDeleteDisabledReason(record)
            : null;
          const editReason = typeof actions.getEditDisabledReason === "function"
            ? actions.getEditDisabledReason(record)
            : null;

          return (
          <Space size="small">
            {actions.onView && (
              <Tooltip title="View">
                <Button
                  type="text"
                  size="small"
                  icon={<EyeOutlined />}
                  onClick={() => actions.onView(record)}
                />
              </Tooltip>
            )}
            {actions.onEdit && actions.canEdit !== false && (
              <Tooltip title={editDisabled ? editReason || "Edit disabled" : "Edit"}>
                <Button
                  type="text"
                  size="small"
                  icon={<EditOutlined />}
                  disabled={editDisabled}
                  onClick={() => actions.onEdit(record)}
                />
              </Tooltip>
            )}
            {actions.onDelete && actions.canDelete !== false && (
              <Tooltip title={deleteDisabled ? deleteReason || "Delete disabled" : "Delete"}>
                <Button
                  type="text"
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  disabled={deleteDisabled}
                  onClick={() => actions.onDelete(record)}
                />
              </Tooltip>
            )}
          </Space>
          );
        },
      }
    : null;

  const allColumns = actionColumn
    ? [...columns, actionColumn]
    : columns;

  // Pagination config
  const paginationConfig = pagination
    ? {
        current: pagination.current || 1,
        pageSize: pagination.pageSize || 5,
        total: pagination.total || 0,
        showSizeChanger: false,
        showTotal: (total, range) =>
          `Showing ${range[0]} - ${range[1]} of ${total} records`,
        onChange: (page, pageSize) => {
          if (onPageChange) onPageChange(page, pageSize);
        },
      }
    : false;

  return (
    <div className="data-table-wrapper">
      <Table
        columns={allColumns}
        dataSource={dataSource}
        loading={loading}
        pagination={paginationConfig}
        rowSelection={rowSelection}
        rowKey={rowKey}
        scroll={scroll || { x: "max-content" }}
        size={size}
        className="data-table"
        {...restProps}
      />
    </div>
  );
}
